import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Divider,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Add,
  Download,
  SailingOutlined,
  FlashOn,
  People,
  Assignment,
  BookOnline,
  FilterList,
  Schedule,
  MonetizationOn,
  Phone,
  Email,
  Edit,
  Visibility,
  Cancel,
  CheckCircle,
  Payment,
  Print,
  ContentCopy,
  Link as LinkIcon,
  WhatsApp,
} from '@mui/icons-material';
import { format, differenceInDays } from 'date-fns';
import { BookingDetailsModal } from '@/components/bookings/BookingDetailsModal.tsx';
import { AdminBookingDetailsModal } from '@/components/bookings/AdminBookingDetailsModal';
import { SmartEditBookingModal } from '@/components/bookings/SmartEditBookingModal';
import CreateYachtBookingDialog from '@/components/bookings/CreateYachtBookingDialog';
import { bookingServiceV2 } from '@/services/bookingService.v2';
import { BookingV2 } from '@/types';
import toast from 'react-hot-toast';
import { buildApiUrl } from '@/utils/apiHelpers';

// Filter values interface
interface FilterValues {
  status: string;
  type: string;
  yachtType: string;
  startDateFrom: string;
  startDateTo: string;
  minAmount: string;
  maxAmount: string;
}

const initialFilterValues: FilterValues = {
  status: '',
  type: '',
  yachtType: '',
  startDateFrom: '',
  startDateTo: '',
  minAmount: '',
  maxAmount: '',
};

export const YachtRentalsPage: React.FC = () => {
  // State management
  const [yachtRentals, setYachtRentals] = useState<BookingV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [adminDetailsOpen, setAdminDetailsOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingBookingId, setEditingBookingId] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBooking, setSelectedBooking] = useState<BookingV2 | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [advancedFilterOpen, setAdvancedFilterOpen] = useState(false);
  const [filterValues, setFilterValues] = useState<FilterValues>(initialFilterValues);
  const [createBookingOpen, setCreateBookingOpen] = useState(false);
  const [paymentLinkDialogOpen, setPaymentLinkDialogOpen] = useState(false);
  const [paymentLinkData, setPaymentLinkData] = useState<{
    paymentUrl: string;
    customerName: string;
    customerEmail: string;
    amount: number;
    isExisting: boolean;
    instructions?: {
      share?: string;
      validity?: string;
      webhook?: string;
    };
  } | null>(null);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptBooking, setReceiptBooking] = useState<BookingV2 | null>(null);
  const [fullBookingData, setFullBookingData] = useState<Map<string, any>>(new Map());

  // Fetch yacht rentals with V2 API and server-side pagination
  const fetchYachtRentals = async () => {
    setLoading(true);
    setError(null);
    try {
      // Map selectedStatus to API status value
      const statusValue = selectedStatus || undefined;

      const result = await bookingServiceV2.getYachtRentals({
        page: page + 1, // Backend uses 1-based pagination, MUI uses 0-based
        limit: rowsPerPage,
        status: statusValue,
      });

      setYachtRentals(result.bookings);
      if (result.pagination) {
        setTotalCount(result.pagination.total);
      }
    } catch (err) {
      setError('Failed to fetch yacht rentals. Please try again.');
      console.error('❌ YachtRentalsPage fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchYachtRentals();
  }, [page, rowsPerPage, selectedStatus]);

  // Filter handlers
  const handleFilterChange = (field: keyof FilterValues, value: string) => {
    setFilterValues(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilter = () => {
    setAdvancedFilterOpen(false);
    setPage(0);
  };

  const handleResetFilter = () => {
    setFilterValues(initialFilterValues);
    setSelectedStatus('');
    setSearchTerm('');
    setSelectedTab(0);
    setPage(0);
  };

  const handleTabChange = (newValue: number) => {
    setSelectedTab(newValue);
    setPage(0);
  };

  // Helper functions
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, booking: BookingV2) => {
    setAnchorEl(event.currentTarget);
    setSelectedBooking(booking);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBooking(null);
  };

  const handleViewDetails = () => {
    if (selectedBooking) {
      setSelectedBookingId(selectedBooking.id);
      setIsModalOpen(true);
    }
    handleMenuClose();
  };

  const handleEditBooking = () => {
    if (selectedBooking) {
      setEditingBookingId(selectedBooking.id);
      setEditModalOpen(true);
    }
    handleMenuClose();
  };

  const handleGeneratePaymentLink = async () => {
    if (!selectedBooking) return;

    handleMenuClose();

    try {
      const bookingId = selectedBooking.id;

      const loadingToast = toast.loading('Generating payment link...');

      const response = await fetch(`${getApiBaseUrl()}/api/admin/bookings/${bookingId}/generate-payment-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          callbackUrl: `${window.location.origin}/payment-success`
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Backend error response:', errorData);
        const errorMessage = errorData.error?.details || errorData.error?.message || errorData.message || 'Failed to generate payment link';
        toast.dismiss(loadingToast);
        throw new Error(errorMessage);
      }

      const result = await response.json();

      const { paymentUrl, customerEmail, customerName, amount, instructions, isExisting } = result.data;

      toast.dismiss(loadingToast);
      await navigator.clipboard.writeText(paymentUrl);

      setPaymentLinkData({
        paymentUrl,
        customerName,
        customerEmail,
        amount,
        isExisting,
        instructions
      });
      setPaymentLinkDialogOpen(true);

      const toastMessage = isExisting ? 'Existing payment link retrieved!' : 'New payment link created!';
      toast.success(toastMessage, { duration: 3000 });

      await fetchYachtRentals();

    } catch (error: any) {
      console.error('❌ Error generating payment link:', error);

      let errorMessage = 'Failed to generate payment link';
      if (error.message.includes('ALREADY_PAID')) {
        errorMessage = 'This booking has already been paid';
      } else if (error.message.includes('BOOKING_CANCELLED')) {
        errorMessage = 'Cannot generate payment link for cancelled booking';
      } else if (error.message.includes('MISSING_EMAIL')) {
        errorMessage = 'Customer email is required to generate payment link';
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, { duration: 5000 });
    }
  };

  const handlePrintReceipt = () => {
    if (!selectedBooking) return;
    setReceiptBooking(selectedBooking); // Save booking before menu closes
    setReceiptDialogOpen(true);
    handleMenuClose();
  };

  const handlePrint = () => {
    if (!receiptBooking) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to print receipts');
      return;
    }

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        minimumFractionDigits: 0
      }).format(amount || 0);
    };

    const receiptHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt - ${receiptBooking.bookingId}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
            color: #333;
          }
          .header {
            text-align: center;
            margin-bottom: 40px;
            padding-bottom: 20px;
            border-bottom: 3px solid #1976d2;
          }
          .company-name {
            font-size: 32px;
            font-weight: 700;
            color: #1976d2;
            margin-bottom: 8px;
          }
          .receipt-title {
            font-size: 20px;
            color: #666;
            margin-bottom: 8px;
          }
          .booking-ref {
            font-size: 18px;
            font-weight: 600;
            color: #333;
          }
          .invoice-meta {
            display: flex;
            justify-content: space-between;
            margin-top: 16px;
            padding-top: 16px;
            border-top: 1px solid #e0e0e0;
          }
          .invoice-meta-item {
            text-align: left;
          }
          .invoice-meta-label {
            font-size: 12px;
            color: #999;
            text-transform: uppercase;
            margin-bottom: 4px;
          }
          .invoice-meta-value {
            font-size: 14px;
            font-weight: 600;
            color: #333;
          }
          .section {
            margin-bottom: 30px;
          }
          .section-title {
            font-size: 16px;
            font-weight: 600;
            color: #1976d2;
            margin-bottom: 12px;
            padding-bottom: 8px;
            border-bottom: 2px solid #e0e0e0;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px solid #f0f0f0;
          }
          .info-label {
            color: #666;
            font-weight: 500;
          }
          .info-value {
            font-weight: 600;
            text-align: right;
          }
          .subtotal-row {
            display: flex;
            justify-content: space-between;
            padding: 12px 0;
            margin-top: 8px;
            border-top: 1px solid #e0e0e0;
            font-weight: 600;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            padding: 16px 0;
            margin-top: 8px;
            border-top: 2px solid #333;
            font-size: 18px;
            font-weight: 700;
            background-color: #f8f9fa;
            margin: 0 -16px;
            padding: 16px;
          }
          .cost-item {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px dotted #e0e0e0;
          }
          .cost-item-label {
            color: #666;
          }
          .cost-item-value {
            font-weight: 600;
          }
          .status-badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 16px;
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
          }
          .status-confirmed {
            background-color: #d4edda;
            color: #155724;
          }
          .status-pending {
            background-color: #fff3cd;
            color: #856404;
          }
          .status-cancelled {
            background-color: #f8d7da;
            color: #721c24;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #e0e0e0;
            text-align: center;
            color: #666;
            font-size: 14px;
          }
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">PolarRide</div>
          <div class="receipt-title">Tax Invoice / Receipt - Yacht Charter</div>
          <div class="booking-ref">Booking Reference: ${receiptBooking.bookingId}</div>
          <div class="invoice-meta">
            <div class="invoice-meta-item">
              <div class="invoice-meta-label">Invoice Number</div>
              <div class="invoice-meta-value">INV-${receiptBooking.bookingId}</div>
            </div>
            <div class="invoice-meta-item">
              <div class="invoice-meta-label">Invoice Date</div>
              <div class="invoice-meta-value">${new Date().toLocaleDateString('en-US', { dateStyle: 'medium' })}</div>
            </div>
            <div class="invoice-meta-item">
              <div class="invoice-meta-label">Charter Status</div>
              <div class="invoice-meta-value">
                <span class="status-badge ${
                  receiptBooking.status === 'confirmed' ? 'status-confirmed' :
                  receiptBooking.status === 'cancelled' ? 'status-cancelled' : 'status-pending'
                }">${receiptBooking.status.toUpperCase()}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Charter Information</div>
          <div class="info-row">
            <span class="info-label">Charter Type:</span>
            <span class="info-value">${
              receiptBooking.charterType === 'destination' && receiptBooking.destination
                ? `${receiptBooking.destination} (${receiptBooking.tripType === 'round_trip' ? 'Round Trip' : 'One Way'})`
                : receiptBooking.charterType === 'time-based' && receiptBooking.rentalOption
                ? receiptBooking.rentalOption.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') + ' Charter'
                : 'Yacht Charter'
            }</span>
          </div>
          <div class="info-row">
            <span class="info-label">Status:</span>
            <span class="info-value">
              <span class="status-badge ${
                receiptBooking.status === 'confirmed' ? 'status-confirmed' :
                receiptBooking.status === 'cancelled' ? 'status-cancelled' : 'status-pending'
              }">${receiptBooking.status}</span>
            </span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Customer Details</div>
          <div class="info-row">
            <span class="info-label">Name:</span>
            <span class="info-value">${receiptBooking.customer.name}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Email:</span>
            <span class="info-value">${receiptBooking.customer.email}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Phone:</span>
            <span class="info-value">${receiptBooking.customer.phone}</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Yacht Details</div>
          <div class="info-row">
            <span class="info-label">Yacht:</span>
            <span class="info-value">${receiptBooking.yacht.name}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Type:</span>
            <span class="info-value">${receiptBooking.yacht.type}</span>
          </div>
          ${receiptBooking.yacht.capacity ? `
          <div class="info-row">
            <span class="info-label">Capacity:</span>
            <span class="info-value">${receiptBooking.yacht.capacity} guests</span>
          </div>` : ''}
          <div class="info-row">
            <span class="info-label">Duration:</span>
            <span class="info-value">${receiptBooking.dateRange.days} day${receiptBooking.dateRange.days !== 1 ? 's' : ''}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Start Date:</span>
            <span class="info-value">${format(new Date(receiptBooking.dateRange.start), 'PPP')}</span>
          </div>
          <div class="info-row">
            <span class="info-label">End Date:</span>
            <span class="info-value">${format(new Date(receiptBooking.dateRange.end), 'PPP')}</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Cost Breakdown</div>
          ${(() => {
            const yacht = receiptBooking as any;
            const pricingBreakdown = yacht.pricingBreakdown || yacht.pricing || {};

            // Calculate costs
            const charterCost = pricingBreakdown.baseRate || pricingBreakdown.baseCost || yacht.amount * 0.80;
            const bookingFee = pricingBreakdown.bookingFee || yacht.amount * 0.05;
            const bookingFeeRate = pricingBreakdown.bookingFeeRate || 5;
            const vat = pricingBreakdown.taxAmount || pricingBreakdown.vat || yacht.amount * 0.075;
            const vatRate = pricingBreakdown.vatRate || 7.5;
            const securityDeposit = pricingBreakdown.securityDeposit || 0;
            const serviceFee = pricingBreakdown.serviceFee || pricingBreakdown.platformFee || yacht.amount * 0.15;
            const serviceFeeRate = pricingBreakdown.serviceFeeRate || 15;
            const hostPayout = pricingBreakdown.hostPayout || pricingBreakdown.yachtOwnerReceives || (charterCost - serviceFee);
            const totalAmount = yacht.amount;

            let html = `
              <div style="margin-bottom: 12px; font-weight: 600; color: #666; font-size: 14px;">Customer Pays:</div>
              <div class="info-row">
                <span class="info-label">Charter Cost</span>
                <span class="info-value">${formatCurrency(charterCost)}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Booking Fee (${bookingFeeRate}%)</span>
                <span class="info-value">${formatCurrency(bookingFee)}</span>
              </div>
              <div class="info-row">
                <span class="info-label">VAT (${vatRate}%)</span>
                <span class="info-value">${formatCurrency(vat)}</span>
              </div>`;

            if (securityDeposit > 0) {
              html += `
              <div class="info-row">
                <span class="info-label" style="color: #ff9800;">Security Deposit (Refundable)</span>
                <span class="info-value" style="color: #ff9800;">${formatCurrency(securityDeposit)}</span>
              </div>`;
            }

            html += `
              <div style="margin: 16px 0; padding: 16px; background-color: #e3f2fd; border-radius: 4px;">
                <div class="info-row" style="border: none; margin: 0;">
                  <span style="font-weight: 700; font-size: 18px;">Total Amount Paid</span>
                  <span style="font-weight: 700; font-size: 18px; color: #1976d2;">${formatCurrency(totalAmount)}</span>
                </div>
              </div>

              <div style="padding: 12px; background-color: #e3f2fd; border-left: 4px solid #2196f3; border-radius: 4px; margin-bottom: 16px;">
                <span style="font-size: 12px; color: #1565c0;">
                  Platform service fee (${serviceFeeRate}%: ${formatCurrency(serviceFee)}) is deducted from yacht owner payout
                </span>
              </div>

              <div style="padding: 16px; background-color: #e8f5e9; border-radius: 4px;">
                <div class="info-row" style="border: none; margin: 0;">
                  <span style="font-weight: 600; color: #2e7d32;">Yacht Owner Receives</span>
                  <span style="font-weight: 700; color: #2e7d32;">${formatCurrency(hostPayout)}</span>
                </div>
              </div>
            `;

            return html;
          })()}
        </div>

        <div style="margin-top: 24px; padding: 16px; background-color: #f5f5f5; border-radius: 4px; text-align: center;">
          <div style="font-size: 12px; color: #666; margin-bottom: 8px;">Payment Status</div>
          <span class="status-badge ${
            receiptBooking.status === 'confirmed' ? 'status-confirmed' : 'status-pending'
          }">
            ${(receiptBooking as any).paymentStatus || receiptBooking.status || 'PENDING'}
          </span>
        </div>

        <div class="footer">
          <p>Thank you for choosing PolarRide!</p>
          <p style="margin-top: 8px;">For support, contact us at support@polarride.com</p>
          <p style="margin-top: 16px; font-size: 12px; color: #999;">
            Generated on ${new Date().toLocaleDateString('en-US', { dateStyle: 'full' })}
          </p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(receiptHtml);
    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const handleExportData = () => {
    // Export functionality
    // TODO: Implement export logic to download the currently displayed yacht rental bookings as a CSV file.
    //       The export should include the following fields: Booking ID, Customer Name, Customer Email, Customer Phone,
    //       Yacht Name, Start Date, End Date, Status, and Total Amount.
  };

  // Status color function
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'cancelled': return 'error';
      case 'pending': return 'warning';
      case 'in-progress': 
      case 'ongoing': return 'info';
      case 'completed': return 'primary';
      case 'declined': return 'error';
      default: return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'instant': return <FlashOn sx={{ fontSize: 16 }} />;
      case 'yacht-charter': 
      case 'yacht_charter': return <SailingOutlined sx={{ fontSize: 16 }} />;
      case 'with-crew': return <People sx={{ fontSize: 16 }} />;
      case 'request-to-book': return <BookOnline sx={{ fontSize: 16 }} />;
      default: return <SailingOutlined sx={{ fontSize: 16 }} />;
    }
  };

  // Filtered data
  const filteredAllBookings = useMemo(() => {
    return yachtRentals.filter(booking => {
      // Search filter - using flat BookingV2 fields
      const customerName = `${booking.user_first_name} ${booking.user_last_name}`.toLowerCase();
      const matchesSearch = searchTerm === '' ||
        customerName.includes(searchTerm.toLowerCase()) ||
        booking.bookingNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (booking.vehicle_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (booking.user_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (booking.user_phone || '').includes(searchTerm);

      // Tab filter
      const matchesTab = (() => {
        switch (selectedTab) {
          case 1: // Active
            return ['confirmed', 'in-progress', 'ongoing', 'active'].includes(booking.status);
          case 2: // Completed
            return booking.status === 'completed';
          case 3: // Pending
            return ['pending', 'payment-pending'].includes(booking.status);
          case 4: // Cancelled
            return ['cancelled', 'declined'].includes(booking.status);
          default: // All
            return true;
        }
      })();

      // Status filter (from status dropdown)
      const matchesStatus = !selectedStatus || booking.status === selectedStatus;

      // Advanced filter
      const matchesAdvancedFilter = (() => {
        // Status filter from advanced dialog
        if (filterValues.status && booking.status !== filterValues.status) return false;

        // Type filter from advanced dialog
        if (filterValues.type && booking.serviceType !== filterValues.type) return false;

        // Yacht type filter - skip since BookingV2 doesn't have yacht type detail
        // if (filterValues.yachtType && booking.yachtType !== filterValues.yachtType) return false;

        // Date range filter
        if (filterValues.startDateFrom && new Date(booking.startDate) < new Date(filterValues.startDateFrom)) return false;
        if (filterValues.startDateTo && new Date(booking.startDate) > new Date(filterValues.startDateTo)) return false;

        // Amount range filter
        const amount = parseFloat(booking.totalAmount);
        if (filterValues.minAmount && amount < parseFloat(filterValues.minAmount)) return false;
        if (filterValues.maxAmount && amount > parseFloat(filterValues.maxAmount)) return false;

        return true;
      })();

      return matchesSearch && matchesTab && matchesStatus && matchesAdvancedFilter;
    });
  }, [yachtRentals, searchTerm, selectedTab, selectedStatus, filterValues]);

  // Pagination
  const paginatedBookings = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredAllBookings.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAllBookings, page, rowsPerPage]);

  // Tab counts
  const allBookings = yachtRentals;
  const activeBookings = allBookings.filter(b => ['confirmed', 'in-progress', 'ongoing'].includes(b.status)).length;
  const completedBookings = allBookings.filter(b => b.status === 'completed').length;
  const pendingBookings = allBookings.filter(b => ['pending', 'payment-pending'].includes(b.status)).length;
  const cancelledBookings = allBookings.filter(b => ['cancelled', 'declined'].includes(b.status)).length;

  // Tab configuration with styling
  const bookingTypes = [
    {
      label: 'All Charters',
      count: allBookings.length,
      icon: <SailingOutlined sx={{ fontSize: 18 }} />,
      bgColor: 'rgba(25, 118, 210, 0.08)',
      borderColor: 'rgba(25, 118, 210, 0.3)',
    },
    {
      label: 'Active',
      count: activeBookings,
      icon: <CheckCircle sx={{ fontSize: 18, color: '#4caf50' }} />,
      bgColor: 'rgba(76, 175, 80, 0.08)',
      borderColor: 'rgba(76, 175, 80, 0.3)',
    },
    {
      label: 'Completed',
      count: completedBookings,
      icon: <Assignment sx={{ fontSize: 18, color: '#2196f3' }} />,
      bgColor: 'rgba(33, 150, 243, 0.08)',
      borderColor: 'rgba(33, 150, 243, 0.3)',
    },
    {
      label: 'Pending',
      count: pendingBookings,
      icon: <Schedule sx={{ fontSize: 18, color: '#ff9800' }} />,
      bgColor: 'rgba(255, 152, 0, 0.08)',
      borderColor: 'rgba(255, 152, 0, 0.3)',
    },
    {
      label: 'Cancelled',
      count: cancelledBookings,
      icon: <Cancel sx={{ fontSize: 18, color: '#f44336' }} />,
      bgColor: 'rgba(244, 67, 54, 0.08)',
      borderColor: 'rgba(244, 67, 54, 0.3)',
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Yacht Charter Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage yacht charters and sailing bookings
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setAdvancedFilterOpen(true)}
          >
            Advanced Filter
          </Button>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              label="Status Filter"
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="in-progress">In Progress</MenuItem>
              <MenuItem value="ongoing">Ongoing</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="declined">Declined</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExportData}
          >
            Export
          </Button>
          
          <Button
            variant="contained"
            startIcon={<Add />}
            color="primary"
            onClick={() => setCreateBookingOpen(true)}
          >
            New Charter
          </Button>
        </Box>
      </Box>

      {/* Filter Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search yachts, customers, or booking details..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1, minWidth: 300 }}
              size="small"
            />
          </Box>

          {/* Booking Type Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
            <Tabs
              value={selectedTab}
              onChange={(_, newValue) => handleTabChange(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {bookingTypes.map((type, index) => (
                <Tab
                  key={index}
                  label={
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5,
                      p: 1,
                      borderRadius: 2,
                      backgroundColor: selectedTab === index ? type.bgColor : 'transparent',
                      border: selectedTab === index ? `1px solid ${type.borderColor}` : '1px solid transparent',
                      transition: 'all 0.2s ease',
                      minWidth: 140
                    }}>
                      {type.icon}
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                          {type.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {type.count} charter{type.count !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  sx={{ 
                    textTransform: 'none',
                    p: 0,
                    '&.Mui-selected': {
                      color: 'inherit'
                    }
                  }}
                />
              ))}
            </Tabs>
          </Box>
        </CardContent>
      </Card>

      {/* Yacht Rentals Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Charter Details</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Yacht</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedBookings.map((rental) => (
                <TableRow
                  key={rental.id}
                  hover
                  onClick={() => {
                    setSelectedBookingId(rental.id);
                    setDrawerOpen(true);
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {getTypeIcon(rental.bookingType)}
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {rental.bookingNumber}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {rental.serviceType || 'Yacht Charter'}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {`${rental.user_first_name} ${rental.user_last_name}`}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                        <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {rental.user_email}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {rental.user_phone || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {rental.vehicle_name || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {rental.serviceType || 'Yacht Charter'}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {differenceInDays(new Date(rental.endDate), new Date(rental.startDate)) || 1} day{differenceInDays(new Date(rental.endDate), new Date(rental.startDate)) !== 1 ? 's' : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(rental.startDate), 'MMM dd')} - {format(new Date(rental.endDate), 'MMM dd, yyyy')}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      label={rental.status.charAt(0).toUpperCase() + rental.status.slice(1).replace('-', ' ')}
                      color={getStatusColor(rental.status) as any}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ₦{parseFloat(rental.totalAmount).toLocaleString('en-NG', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuClick(e, rental);
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
          showFirstButton
          showLastButton
        />
      </Card>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <Visibility sx={{ mr: 1, fontSize: 18 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleGeneratePaymentLink}>
          <Payment sx={{ mr: 1, fontSize: 18 }} />
          Generate Payment Link
        </MenuItem>
        <MenuItem onClick={handlePrintReceipt}>
          <Print sx={{ mr: 1, fontSize: 18 }} />
          Print Receipt
        </MenuItem>
        <MenuItem onClick={handleEditBooking}>
          <Edit sx={{ mr: 1, fontSize: 18 }} />
          Edit Charter
        </MenuItem>
      </Menu>

      {/* Modals */}
      {isModalOpen && selectedBookingId && (
        <BookingDetailsModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          bookingId={selectedBookingId}
        />
      )}

      {editModalOpen && editingBookingId && (
        <SmartEditBookingModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSuccess={() => {
            setEditModalOpen(false);
            fetchYachtRentals(); // Refresh data
          }}
          bookingId={editingBookingId}
        />
      )}

      {/* Advanced Filter Dialog */}
      <Dialog open={advancedFilterOpen} onClose={() => setAdvancedFilterOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Advanced Filter Options</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Charter Status</InputLabel>
                <Select 
                  value={filterValues.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Charter Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in-progress">In Progress</MenuItem>
                  <MenuItem value="ongoing">Ongoing</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="declined">Declined</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Charter Type</InputLabel>
                <Select 
                  value={filterValues.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  label="Charter Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="yacht-charter">Yacht Charter</MenuItem>
                  <MenuItem value="yacht_charter">Yacht Charter (Legacy)</MenuItem>
                  <MenuItem value="instant">Instant Charter</MenuItem>
                  <MenuItem value="request-to-book">Request to Book</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Yacht Type</InputLabel>
                <Select 
                  value={filterValues.yachtType}
                  onChange={(e) => handleFilterChange('yachtType', e.target.value)}
                  label="Yacht Type"
                >
                  <MenuItem value="">All Yacht Types</MenuItem>
                  <MenuItem value="yacht">Luxury Yacht</MenuItem>
                  <MenuItem value="Yacht">Yacht</MenuItem>
                  <MenuItem value="sailboat">Sailboat</MenuItem>
                  <MenuItem value="catamaran">Catamaran</MenuItem>
                  <MenuItem value="motorboat">Motorboat</MenuItem>
                  <MenuItem value="boat">Boat</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Charter Date From"
                value={filterValues.startDateFrom}
                onChange={(e) => handleFilterChange('startDateFrom', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Charter Date To"
                value={filterValues.startDateTo}
                onChange={(e) => handleFilterChange('startDateTo', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Min Amount (₦)"
                value={filterValues.minAmount}
                onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                InputProps={{ startAdornment: '₦' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Max Amount (₦)"
                value={filterValues.maxAmount}
                onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                InputProps={{ startAdornment: '₦' }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdvancedFilterOpen(false)}>Cancel</Button>
          <Button variant="outlined" onClick={handleResetFilter}>Reset Filters</Button>
          <Button variant="contained" onClick={handleApplyFilter}>Apply Filters</Button>
        </DialogActions>
      </Dialog>

      {/* Quick Preview - Using Admin Details Modal */}
      {drawerOpen && selectedBookingId && (
        <AdminBookingDetailsModal
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setSelectedBookingId(null);
          }}
          bookingId={selectedBookingId}
        />
      )}

      {/* Admin Booking Details Modal */}
      <AdminBookingDetailsModal
        open={adminDetailsOpen}
        onClose={() => {
          setAdminDetailsOpen(false);
          setSelectedBookingId(null);
        }}
        bookingId={selectedBookingId}
        onBookingUpdated={() => {
          fetchYachtRentals();
        }}
      />

      {/* Create Yacht Booking Dialog */}
      <CreateYachtBookingDialog
        open={createBookingOpen}
        onClose={() => setCreateBookingOpen(false)}
        onSuccess={() => {
          setCreateBookingOpen(false);
          fetchYachtRentals(); // Refresh bookings list
        }}
        initialBookingType="time-based"
      />

      {/* Receipt Dialog */}
      <Dialog
        open={receiptDialogOpen}
        onClose={() => {
          setReceiptDialogOpen(false);
          setReceiptBooking(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5">Payment Receipt</Typography>
            <Typography variant="caption">PolarRide Yacht Charter</Typography>
          </Box>
          <Assignment sx={{ fontSize: 40, opacity: 0.7 }} />
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          {receiptBooking && (() => {
            // Get full booking data with all fields (including cost breakdown)
            const fullBooking = fullBookingData.get(receiptBooking.bookingId) || receiptBooking;


            // Get cost breakdown from payment.breakdown (new backend format) or adminNotes (legacy)
            let costBreakdown: any = null;

            // Prefer payment.breakdown from new backend format
            if (fullBooking.payment?.breakdown) {
              costBreakdown = fullBooking.payment.breakdown;
            } else if (fullBooking.costBreakdown) {
              costBreakdown = fullBooking.costBreakdown;
            } else if (fullBooking.pricingBreakdown) {
              costBreakdown = fullBooking.pricingBreakdown;
            } else if (fullBooking.pricing) {
              costBreakdown = fullBooking.pricing;
            } else {
              // Fallback to parsing adminNotes for legacy bookings
              try {
                const notes = fullBooking.adminNotes;
                const parsed = typeof notes === 'string' ? JSON.parse(notes) : notes;
                costBreakdown = parsed?.costBreakdown;
                if (costBreakdown) {
                }
              } catch (e) {
                console.error('❌ Failed to parse adminNotes:', e);
              }
            }

            if (!costBreakdown) {
            }

            // Calculate service fee ONLY if not provided (backend is source of truth)
            if (costBreakdown && !costBreakdown.serviceFee && costBreakdown.rentalCost) {
              const serviceFeeRate = costBreakdown.serviceFeeRate || 15;
              costBreakdown.serviceFee = Math.round(costBreakdown.rentalCost * (serviceFeeRate / 100));
            }

            // Calculate host payout if not provided
            if (costBreakdown && !costBreakdown.hostPayout && costBreakdown.rentalCost && costBreakdown.serviceFee) {
              costBreakdown.hostPayout = costBreakdown.rentalCost - costBreakdown.serviceFee;
            }

            return (
              <Box id="receipt-content">
                {/* Booking Info */}
                <Grid container spacing={2} sx={{ mb: 3, p: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Booking ID</Typography>
                    <Typography variant="body1" fontWeight="bold">{receiptBooking.bookingId}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Date</Typography>
                    <Typography variant="body1">{new Date().toLocaleDateString()}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Customer</Typography>
                    <Typography variant="body1">{receiptBooking.customer.name}</Typography>
                    <Typography variant="caption" display="block" color="text.secondary">{receiptBooking.customer.email}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Yacht</Typography>
                    <Typography variant="body1">{receiptBooking.yacht.name}</Typography>
                    {receiptBooking.yacht.type && (
                      <Typography variant="caption" display="block" color="text.secondary">{receiptBooking.yacht.type}</Typography>
                    )}
                  </Grid>
                </Grid>

                {/* Rental Period */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Charter Period</Typography>
                  <Typography variant="body2">
                    {format(new Date(receiptBooking.dateRange.start), 'PPP')} to {format(new Date(receiptBooking.dateRange.end), 'PPP')}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Duration: {receiptBooking.dateRange.days} day{receiptBooking.dateRange.days !== 1 ? 's' : ''}
                    {receiptBooking.charterType === 'destination' && receiptBooking.destination
                      ? ` • ${receiptBooking.destination} (${receiptBooking.tripType === 'round_trip' ? 'Round Trip' : 'One Way'})`
                      : receiptBooking.charterType === 'time-based' && receiptBooking.rentalOption
                      ? ` • ${receiptBooking.rentalOption.replace('_', ' ').split(' ').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} Charter`
                      : ''}
                  </Typography>
                </Box>

                {/* Airport Transfer Details - Show only for actual airport transfers */}
                {(fullBooking.serviceType?.toLowerCase().includes('airport') ||
                  (fullBooking.tripType && (fullBooking.tripType === 'airport_pickup' || fullBooking.tripType === 'airport_dropoff')) ||
                  fullBooking.airport) && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'primary.main', borderRadius: 1 }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="bold">
                        Airport Transfer Details
                      </Typography>
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Airport</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {fullBooking.airport?.name ||
                             fullBooking.airport?.airportName ||
                             fullBooking.serviceLocation ||
                             'Not specified'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary">Trip Type</Typography>
                          <Typography variant="body2">
                            {fullBooking.tripType === 'airport_pickup' ? 'Airport Pickup' :
                             fullBooking.tripType === 'airport_dropoff' ? 'Airport Dropoff' :
                             'Airport Transfer'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary">Customer Zone</Typography>
                          <Typography variant="body2">
                            {fullBooking.zone?.name ||
                             fullBooking.zone?.zoneName ||
                             fullBooking.pickupZone ||
                             fullBooking.dropoffZone ||
                             'Not specified'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Cost Breakdown */}
                <Typography variant="h6" sx={{ mb: 2 }}>Cost Breakdown</Typography>

                {costBreakdown ? (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Customer Pays:</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Charter Cost</Typography>
                      <Typography variant="body2">₦{(costBreakdown.rentalCost || costBreakdown.baseRate || costBreakdown.baseCost)?.toLocaleString() || '0'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Booking Fee ({costBreakdown.bookingFeeRate || 5}%)</Typography>
                      <Typography variant="body2">₦{costBreakdown.bookingFee?.toLocaleString() || '0'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">VAT ({costBreakdown.vatRate || 7.5}%)</Typography>
                      <Typography variant="body2">₦{(costBreakdown.vat || costBreakdown.taxAmount)?.toLocaleString() || '0'}</Typography>
                    </Box>
                    {costBreakdown.securityDeposit && costBreakdown.securityDeposit > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="warning.main">Security Deposit (Refundable)</Typography>
                        <Typography variant="body2" color="warning.main">₦{costBreakdown.securityDeposit?.toLocaleString() || '0'}</Typography>
                      </Box>
                    )}
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, bgcolor: 'primary.main', color: 'primary.contrastText', p: 1.5, borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ color: 'inherit' }}>Total Amount Paid</Typography>
                      <Typography variant="h6" sx={{ color: 'inherit', fontWeight: 'bold' }}>₦{(costBreakdown.totalAmount || receiptBooking.amount)?.toLocaleString() || '0'}</Typography>
                    </Box>

                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="caption">
                        Platform service fee ({costBreakdown.serviceFeeRate || 15}%: ₦{(costBreakdown.serviceFee || costBreakdown.platformFee)?.toLocaleString() || '0'}) is deducted from yacht owner payout
                      </Typography>
                    </Alert>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: 'success.main', color: 'success.contrastText', p: 1.5, borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ color: 'inherit' }}>Yacht Owner Receives</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: 'inherit' }}>
                        ₦{(costBreakdown.hostPayout || costBreakdown.yachtOwnerReceives)?.toLocaleString() || '0'}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="caption">
                        Detailed cost breakdown not available for this booking.
                      </Typography>
                    </Alert>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, bgcolor: 'primary.main', color: 'primary.contrastText', p: 1.5, borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ color: 'inherit' }}>Total Amount Paid</Typography>
                      <Typography variant="h6" sx={{ color: 'inherit', fontWeight: 'bold' }}>₦{receiptBooking.amount?.toLocaleString() || '0'}</Typography>
                    </Box>
                  </Box>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Payment Status */}
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">Payment Status</Typography>
                  <Chip
                    label={fullBooking.paymentStatus || receiptBooking.status || 'Pending'}
                    color={fullBooking.paymentStatus === 'paid' || receiptBooking.status === 'confirmed' ? 'success' : 'warning'}
                    sx={{ mt: 1 }}
                  />
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
                  Thank you for choosing PolarRide!
                </Typography>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => {
              setReceiptDialogOpen(false);
              setReceiptBooking(null);
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={handlePrint}
          >
            Print Receipt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Link Dialog */}
      <Dialog
        open={paymentLinkDialogOpen}
        onClose={() => setPaymentLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {paymentLinkData?.isExisting ? (
              <>
                <LinkIcon color="primary" />
                <Typography variant="h6">Existing Payment Link Retrieved</Typography>
              </>
            ) : (
              <>
                <CheckCircle color="success" />
                <Typography variant="h6">Payment Link Generated</Typography>
              </>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {paymentLinkData && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              {/* Customer Info */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Customer Details
                </Typography>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Name:</Typography>
                      <Typography variant="body2" fontWeight={600}>{paymentLinkData.customerName}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Email:</Typography>
                      <Typography variant="body2">{paymentLinkData.customerEmail}</Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Amount:</Typography>
                      <Typography variant="h6" color="primary.main" fontWeight={700}>
                        ₦{paymentLinkData.amount.toLocaleString()}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Box>

              {/* Payment Link */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Payment Link
                </Typography>
                <Card variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Stack spacing={1}>
                    <Typography
                      variant="body2"
                      sx={{
                        wordBreak: 'break-all',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        color: 'primary.main'
                      }}
                    >
                      {paymentLinkData.paymentUrl}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopy />}
                      onClick={async () => {
                        await navigator.clipboard.writeText(paymentLinkData.paymentUrl);
                        toast.success('Link copied to clipboard!');
                      }}
                      fullWidth
                    >
                      Copy Link
                    </Button>
                  </Stack>
                </Card>
              </Box>

              {/* Instructions */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Next Steps
                </Typography>
                <Stack spacing={1}>
                  <Chip
                    icon={<WhatsApp />}
                    label={paymentLinkData.instructions?.share || 'Share this link with the customer via WhatsApp, SMS, or Email'}
                    variant="outlined"
                    color="success"
                    sx={{ justifyContent: 'flex-start', height: 'auto', py: 1.5, px: 1 }}
                  />
                  <Chip
                    icon={<Schedule />}
                    label={paymentLinkData.instructions?.validity || 'Link is valid until payment is completed'}
                    variant="outlined"
                    color="info"
                    sx={{ justifyContent: 'flex-start', height: 'auto', py: 1.5, px: 1 }}
                  />
                  <Chip
                    icon={<CheckCircle />}
                    label={paymentLinkData.instructions?.webhook || 'Payment status will update automatically when customer pays'}
                    variant="outlined"
                    color="primary"
                    sx={{ justifyContent: 'flex-start', height: 'auto', py: 1.5, px: 1 }}
                  />
                </Stack>
              </Box>

              {/* Quick Actions */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Quick Share
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<WhatsApp />}
                    color="success"
                    onClick={() => {
                      const message = `Hi ${paymentLinkData.customerName}, here's your payment link for ₦${paymentLinkData.amount.toLocaleString()}: ${paymentLinkData.paymentUrl}`;
                      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
                    }}
                  >
                    WhatsApp
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Email />}
                    onClick={() => {
                      const subject = 'PolarRide Payment Link';
                      const body = `Hi ${paymentLinkData.customerName},\n\nPlease complete your payment of ₦${paymentLinkData.amount.toLocaleString()} using this link:\n\n${paymentLinkData.paymentUrl}\n\nThank you!`;
                      window.open(`mailto:${paymentLinkData.customerEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
                    }}
                  >
                    Email
                  </Button>
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setPaymentLinkDialogOpen(false)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default YachtRentalsPage;